import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Card11 from '../components/Card11';
import Card12 from '../components/Card12';
import Card13 from '../components/Card13';
import Card14 from '../components/Card14';
import Card28 from '../components/Card28';
import Graph1 from '../components/Graph1';
import Camembert1 from '../components/Camembert1';
import './Relationclient.css';

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Cartes du haut : source chargée au clic + titre du tableau
const CARTES = [
  { source: 'offre',               titre: 'Offres clients',          Component: Card11 },
  { source: 'commande_en_rupture', titre: 'Commandes en rupture',    Component: Card12 },
  { source: 'commande_a_10_jours', titre: 'Commandes à 10 jours',    Component: Card13 },
  { source: 'commande_en_retard',  titre: 'Commandes en retard',     Component: Card14 },
  { source: 'taches',              titre: 'Tâches ADV',              Component: Card28 },
];

// ★ RÉGLAGES LARGEURS — Card12 (commande_en_rupture)
const COLUMN_WIDTHS_RUPTURE = {
  'Horizon':             '90px',
  'MAD':                '130px',
  'Item':               '160px',
  'Description':        '280px',
  'Commande':           '100px',
  'Numero de commande': '140px',
  'Numero de ligne':     '90px',
  'Client':             '140px',
  'Stock':               '80px',
  'Rupture':             '90px',
};
const DEFAULT_WIDTH_RUPTURE = '120px';

// ★ RÉGLAGES LARGEURS — Card14 (commande_en_retard)
const COLUMN_WIDTHS_RETARD = {
  'Horizon':                  '90px',
  'Date de livraison prevue': '140px',
  'Client':                   '140px',
  'Numero de commande':       '140px',
  'Numero de ligne':           '90px',
  'Item':                     '160px',
  'Description':              '280px',
  'Commande':                 '100px',
};
const DEFAULT_WIDTH_RETARD = '120px';

const COLONNES_OFFRE = [
  "Numéro d'offre", 'Client', 'Date de création',
  'Date de Validité', 'Date demandée', 'Ptot', 'Statut',
];

const COLONNES_MASQUEES = ['ID', 'Client_id', 'Departement', 'Département'];

// ★ Colonnes SANS champ de filtre, tableau par tableau
//   (nom de colonne OU libellé affiché, sans accents ni majuscules — '*' = aucun filtre)
const SANS_FILTRE = {
  offre:               ['date de creation', 'date demandee', 'ptot'],
  commande_en_rupture: ['numero de ligne', 'n° ligne', 'n° de ligne', 'commande', 'qte commandee', 'stock', 'rupture'],
  commande_a_10_jours: ['date de livraison prevue', 'statut'],
  commande_en_retard:  ['description', 'commande', 'qte commandee', 'quantite commandee'],
  taches:              '*',
};

const normaliser = (t) => String(t || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ');

// ★ Libellés reconnus dans la description des tâches (découpée en champs)
const CLES_DESCRIPTION = [
  'Date de livraison prevue', 'Date de livraison prévue', 'Item', 'Commande client', 'Ligne',
  'Commande fournisseur', 'Nom fournisseur', 'Date de réception', 'Date de reception', 'Commentaire',
];

// Colonnes alignées à droite (quantités / montants)
const COLONNES_NUMERIQUES = ['Commande', 'Stock', 'Rupture', 'Ptot', 'RAL', 'Quantite', 'Quantité'];

/* ===================================================
   UTILITAIRES
   =================================================== */
const formatDate = (value) => {
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const slug = (col) => col.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const Badge = ({ tone, children }) => <span className={`rc-badge rc-badge-${tone}`}>{children}</span>;

// Découpe "Item : X Commande client : 755 Ligne : 3 …" en [{ label, valeur }]
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REGEX_CLES = new RegExp(
  `(${[...CLES_DESCRIPTION].sort((a, b) => b.length - a.length).map(echapper).join('|')})\\s*:`,
  'g'
);

const decouperDescription = (texte) => {
  if (texte == null) return null;
  const s = String(texte);
  const positions = [];
  let m;
  REGEX_CLES.lastIndex = 0;
  while ((m = REGEX_CLES.exec(s)) !== null) {
    positions.push({ label: m[1], debut: m.index, finLabel: REGEX_CLES.lastIndex });
  }
  if (positions.length < 2) return null; // pas assez structuré : on garde le texte brut
  const champs = [];
  const avant = s.slice(0, positions[0].debut).trim();
  if (avant) champs.push({ label: 'Info', valeur: avant });
  positions.forEach((p, i) => {
    const fin = i + 1 < positions.length ? positions[i + 1].debut : s.length;
    const valeur = s.slice(p.finLabel, fin).trim();
    champs.push({ label: p.label.replace('prevue', 'prévue').replace('reception', 'réception'), valeur: valeur || '—' });
  });
  return champs;
};

// Zone de commentaire qui s'agrandit pour afficher tout le texte
const AutoTextarea = ({ value, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} value={value} rows={2} {...props} />;
};

/* ===================================================
   COMPOSANT
   =================================================== */
const Relationclient = () => {
  useEffect(() => {
    document.title = 'MYSSA – Relation client';
  }, []);

  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [currentSource, setCurrentSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [filtres, setFiltres] = useState({});   // { colonne: texte }

  /* ---------- Chargement ---------- */
  const handleCardClick = (source) => {
    setCurrentSource(source);
    setFiltres({});
    setLoading(true);
    setErreur('');
    setShowTable(true);

    let url;
    if (['commande_a_10_jours', 'commande_en_retard', 'commande_en_rupture', 'offre', 'taches'].includes(source)) {
      url = `${API}/${source}`;
    } else {
      url = process.env.PUBLIC_URL + '/' + source;
    }

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Erreur réseau: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (source === 'commande_a_10_jours' && Array.isArray(data)) {
          data = [...data].sort((a, b) =>
            new Date(a['Date de livraison prevue']) - new Date(b['Date de livraison prevue'])
          );
        }
        if (source === 'taches') {
          data = data
            .filter(row =>
              String(row['Departement'] || row['Département']).toUpperCase() === 'ADV' &&
              ['ouvert', 'en cours'].includes(String(row['Statut'] || '').toLowerCase())
            )
            .sort((a, b) => new Date(a['Date'] || 0) - new Date(b['Date'] || 0));
        }
        setTableData(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(`Erreur de chargement ${source}:`, err);
        setTableData([]);
        setErreur('Impossible de charger les données.');
      })
      .finally(() => setLoading(false));
  };

  const fermerTableau = () => {
    setShowTable(false);
    setCurrentSource('');
    setTableData([]);
    setFiltres({});
  };

  /* ---------- Mise à jour d'une tâche (statut / commentaire) ---------- */
  const majLigne = (numeroTache, champ, valeur) => {
    setTableData(prev => prev.map(r => (r['Numero tache'] === numeroTache ? { ...r, [champ]: valeur } : r)));
  };

  const sauverTache = async (numeroTache, commentaire, statut) => {
    try {
      await fetch(`${API}/taches/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_tache: numeroTache, commentaire, statut }),
      });
      if (String(numeroTache).startsWith('A')) {
        await fetch(`${API}/base_commentaire_animation/update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ numero_tache: numeroTache, commentaire }),
        });
      }
    } catch (err) {
      console.error('Erreur sauvegarde tâche :', err);
    }
  };

  /* ---------- Filtre de recherche ---------- */
  const filtresActifs = Object.entries(filtres).filter(([, v]) => v && v.trim() !== '');

  const lignesFiltrees = useMemo(() => {
    if (!filtresActifs.length) return tableData;
    return tableData.filter(row =>
      filtresActifs.every(([col, q]) => {
        const brut = row[col];
        if (brut == null) return false;
        const texte = String(brut).toLowerCase();
        const affiche = col.toLowerCase().includes('date') || col === 'MAD' ? String(formatDate(brut)).toLowerCase() : texte;
        const cherche = q.trim().toLowerCase();
        return texte.includes(cherche) || affiche.includes(cherche);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, filtres]);

  const setFiltre = (col, valeur) => setFiltres(prev => ({ ...prev, [col]: valeur }));
  const effacerFiltres = () => setFiltres({});

  // ★ En-têtes centrés au milieu de la colonne (style inline : prioritaire sur Relationclient.css,
  //   y compris sur la classe "num" qui aligne les colonnes numériques à droite)
  const STYLE_TH       = { textAlign: 'center', verticalAlign: 'middle' };
  const STYLE_TH_LABEL = { textAlign: 'center', justifyContent: 'center', width: '100%' };
  const STYLE_FILTRE   = { textAlign: 'center' };

  // En-tête de colonne : libellé + champ de filtre
  const renderTh = (col, label, extraClass = '') => {
    const regle = SANS_FILTRE[currentSource];
    const sansFiltre = regle === '*' ||
      (Array.isArray(regle) && (regle.includes(normaliser(col)) || regle.includes(normaliser(label))));
    if (sansFiltre) {
      return (
        <th key={col} className={`${extraClass} rc-th-nofilter`} style={STYLE_TH}>
          <div className="rc-th-label" style={STYLE_TH_LABEL}>{label}</div>
        </th>
      );
    }
    return (
    <th key={col} className={extraClass} style={STYLE_TH}>
      <div className="rc-th-label" style={STYLE_TH_LABEL}>{label}</div>
      <input
        type="text"
        style={STYLE_FILTRE}
        className={`rc-filter ${filtres[col] ? 'active' : ''}`}
        value={filtres[col] || ''}
        onChange={(e) => setFiltre(col, e.target.value)}
        placeholder="Filtrer…"
        aria-label={`Filtrer la colonne ${label}`}
      />
    </th>
    );
  };

  const carteCourante = CARTES.find(c => c.source === currentSource);

  /* ===================================================
     TABLEAU OFFRES (Card11)
     =================================================== */
  const renderTableOffre = () => (
    <table className="rc-table">
      <thead>
        <tr>{COLONNES_OFFRE.map(col => renderTh(col, col, COLONNES_NUMERIQUES.includes(col) ? 'num' : ''))}</tr>
      </thead>
      <tbody>
        {lignesFiltrees.map((row, i) => (
          <tr key={i}>
            {COLONNES_OFFRE.map(col => {
              let value = row[col];

              if (col === 'Statut') {
                if (value === 'Analyse de faisabilité') return <td key={col}><Badge tone="ok">{value}</Badge></td>;
                if (value === 'Attente signature client') return <td key={col}><Badge tone="warn">{value}</Badge></td>;
                return <td key={col}>{value ? <Badge tone="neutral">{value}</Badge> : '—'}</td>;
              }

              if (col === 'Date de Validité' && value) {
                const expiree = new Date(value) < new Date();
                return (
                  <td key={col} className="rc-mono">
                    {expiree ? <Badge tone="danger">{formatDate(value)} · expirée</Badge> : formatDate(value)}
                  </td>
                );
              }

              if (value && ['Date de création', 'Date demandée'].includes(col)) {
                return <td key={col} className="rc-mono">{formatDate(value)}</td>;
              }

              if (col === 'Ptot' && value != null) {
                return (
                  <td key={col} className="num rc-mono">
                    {Number(value).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </td>
                );
              }

              return <td key={col} className={col === "Numéro d'offre" ? 'rc-mono rc-strong' : ''}>{value}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  /* ===================================================
     TABLEAU GÉNÉRIQUE (Card12, Card13, Card14, Card28)
     =================================================== */
  const renderTable = () => {
    const columns = Object.keys(tableData[0] || {}).filter(col => !COLONNES_MASQUEES.includes(col));

    const columnLabels =
      currentSource === 'commande_en_rupture'
        ? { MAD: 'Date de livraison prévue', Description: 'Description', Commande: 'Qté commandée' }
        : currentSource === 'commande_a_10_jours'
        ? { 'Numero de commande': 'Numéro de commande', 'Date de livraison prevue': 'Date de livraison prévue' }
        : currentSource === 'commande_en_retard'
        ? {
            'Numero de commande': 'Numéro de commande',
            'Date de livraison prevue': 'Date de livraison prévue',
            'Numero de ligne': 'N° de ligne',
            Description: 'Description',
            Commande: 'Qté commandée',
          }
        : {};

    const widthMap =
      currentSource === 'commande_en_rupture' ? COLUMN_WIDTHS_RUPTURE :
      currentSource === 'commande_en_retard' ? COLUMN_WIDTHS_RETARD :
      null;

    const defaultW =
      currentSource === 'commande_en_rupture' ? DEFAULT_WIDTH_RUPTURE :
      currentSource === 'commande_en_retard' ? DEFAULT_WIDTH_RETARD :
      null;

    const tableTotal = widthMap
      ? columns.reduce((s, c) => s + parseInt(widthMap[c] || defaultW, 10), 0)
      : null;

    const renderCell = (row, col) => {
      let value = row[col];
      const key = col;
      const estNum = COLONNES_NUMERIQUES.includes(col);

      // Horizon : pastille de couleur
      if (col === 'Horizon' || col === 'horizon') {
        const tone = value === 'Retard' ? 'danger' : value === 'J' ? 'warn' : 'ok';
        return <td key={key}><Badge tone={tone}>{value}</Badge></td>;
      }

      // Dates
      if (value && (col.toLowerCase().includes('date') || (currentSource === 'commande_en_rupture' && col === 'MAD'))) {
        return <td key={key} className="rc-mono">{formatDate(value)}</td>;
      }

      // Rupture : quantités arrondies et pastilles
      if (currentSource === 'commande_en_rupture' && ['Commande', 'Stock', 'Rupture'].includes(col)) {
        if (value !== null && value !== undefined && value !== '') value = Math.round(Number(value));
        if (col === 'Rupture') return <td key={key} className="num"><Badge tone="danger">{value}</Badge></td>;
        if (col === 'Stock' && Number(value) > 0) return <td key={key} className="num"><Badge tone="warn">{value}</Badge></td>;
        return <td key={key} className="num rc-mono">{value}</td>;
      }

      // Statut
      if (col === 'Statut') {
        if (currentSource === 'commande_a_10_jours') {
          // ★ "Couvert par le stock" en vert pastel (distinct du vert franc de "Préparée")
          if (value === 'Couvert par le stock') {
            return (
              <td key={key}>
                <span className="rc-badge" style={{ background: '#E3F4E8', color: '#2F7A4A', border: '1px solid #BFE3CB' }}>{value}</span>
              </td>
            );
          }
          const tone =
            value === 'Préparée' ? 'ok' :
            value === 'Risque de rupture' ? 'danger' : 'neutral';
          return <td key={key}>{value ? <Badge tone={tone}>{value}</Badge> : '—'}</td>;
        }
        // Tâches : statut modifiable
        const statutClasse =
          value === 'ouvert' ? 'rc-statut-ouvert' :
          value === 'en cours' ? 'rc-statut-encours' :
          value === 'soldé' ? 'rc-statut-solde' : '';
        return (
          <td key={key}>
            <select
              className={`rc-select ${statutClasse}`}
              value={value || 'ouvert'}
              aria-label={`Statut de la tâche ${row['Numero tache'] || ''}`}
              onChange={(e) => {
                const newStatut = e.target.value;
                majLigne(row['Numero tache'], 'Statut', newStatut);
                sauverTache(row['Numero tache'], row['Commentaires'] || '', newStatut);
              }}
            >
              <option value="ouvert">ouvert</option>
              <option value="en cours">en cours</option>
              <option value="soldé">soldé</option>
            </select>
          </td>
        );
      }

      // Commentaires (tâches) : sauvegarde à la sortie du champ
      if (col === 'Commentaires') {
        return (
          <td key={key} className="rc-td-wide">
            <AutoTextarea
              className="rc-textarea"
              value={value || ''}
              placeholder="Ajouter un commentaire"
              aria-label={`Commentaire de la tâche ${row['Numero tache'] || ''}`}
              onChange={(e) => majLigne(row['Numero tache'], 'Commentaires', e.target.value)}
              onBlur={(e) => sauverTache(row['Numero tache'], e.target.value || '', row['Statut'] || 'ouvert')}
            />
          </td>
        );
      }

      // Description : retour à la ligne + mise en avant du commentaire
      if (col === 'Description' && currentSource === 'taches') {
        const champs = decouperDescription(value);
        if (champs) {
          return (
            <td key={key} className="rc-td-desc">
              <div className="rc-desc-grid">
                {champs.map((c, idx) => (
                  <div key={idx} className={`rc-desc-field ${c.label === 'Commentaire' ? 'rc-desc-comment' : ''} ${c.valeur === '—' || /^-+$/.test(c.valeur) || /pas de/i.test(c.valeur) ? 'rc-desc-empty' : ''}`}>
                    <span className="rc-desc-label">{c.label}</span>
                    <span className="rc-desc-value">{/^-+$/.test(c.valeur) ? '—' : c.valeur}</span>
                  </div>
                ))}
              </div>
            </td>
          );
        }
      }

      if (col === 'Description') {
        return (
          <td key={key} className="rc-td-wrap">
            {value != null && String(value).includes('Commentaire :') ? (
              String(value)
                .split(/(Commentaire\s*:\s*[\s\S]*)/)
                .map((part, idx) =>
                  /Commentaire\s*:\s*[\s\S]+/.test(part)
                    ? <span key={idx} className="rc-commentaire">{part}</span>
                    : <span key={idx}>{part}</span>
                )
            ) : value}
          </td>
        );
      }

      // Identifiants en police mono
      if (['Item', 'Numero de commande', 'Numero tache', 'Numero de ligne'].includes(col)) {
        return <td key={key} className={`rc-mono ${col === 'Item' ? 'rc-strong' : ''}`}>{value}</td>;
      }

      return <td key={key} className={estNum ? 'num rc-mono' : ''}>{value}</td>;
    };

    return (
      <table
        className={`rc-table ${currentSource === 'taches' ? 'rc-table-taches' : ''}`}
        style={widthMap ? { tableLayout: 'fixed', width: `${tableTotal}px`, minWidth: '100%' } : {}}
      >
        {widthMap && (
          <colgroup>
            {columns.map(c => <col key={c} width={widthMap[c] || defaultW} />)}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map(col =>
              renderTh(col, columnLabels[col] || col, `col-${slug(col)} ${COLONNES_NUMERIQUES.includes(col) ? 'num' : ''}`)
            )}
          </tr>
        </thead>
        <tbody>
          {lignesFiltrees.map((row, i) => (
            <tr key={row['Numero tache'] || i}>
              {columns.map(col => renderCell(row, col))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className="rc-page">
      {/* ★ Données centrées dans toutes les cellules (prioritaire sur Relationclient.css, y compris la classe "num").
            Exception : le bloc multi-champs de la description des tâches reste aligné à gauche pour la lisibilité. */}
      <style>{`
        .rc-table td, .rc-table td.num { text-align: center !important; vertical-align: middle; }
        .rc-table td.rc-td-desc { text-align: left !important; }
      `}</style>
      <div className="rc-container">

        {/* ── Header ── */}
        <div className="rc-header">
          <div className="rc-header-left">
            <div className="rc-title-row">
              <div className="rc-accent" />
              <h1 className="rc-title">Relation client</h1>
              <span className="rc-chip-dept">ADV</span>
            </div>
           
          </div>

          <a
            className="rc-btn rc-btn-ghost"
            href="/mode_emploi_relation_client.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Mode d'emploi
          </a>
        </div>

        {/* ── Cartes ── */}
        <div className="rc-cards" role="group" aria-label="Choisir un tableau">
          {CARTES.map(({ source, titre, Component }) => {
            const actif = currentSource === source;
            return (
              <button
                key={source}
                type="button"
                className={`rc-card-btn ${actif ? 'active' : ''}`}
                aria-pressed={actif}
                aria-label={titre}
                onClick={() => handleCardClick(source)}
              >
                <Component />
              </button>
            );
          })}
        </div>

        {/* ── Graphiques (vue par défaut) ── */}
        {!showTable && (
          <div className="rc-graphs">
            <div className="rc-card rc-graph-card rc-graph-bar"><Graph1 /></div>
            <div className="rc-card rc-graph-card rc-graph-pie"><Camembert1 /></div>
          </div>
        )}

        {/* ── Tableau ── */}
        {showTable && (
          <div className="rc-card rc-table-card">
            <div className="rc-table-head">
              <div className="rc-table-head-left">
                <div className="rc-table-title">{carteCourante?.titre || 'Détail'}</div>
                {!loading && !erreur && (
                  <span className="rc-count">
                    {lignesFiltrees.length}{filtresActifs.length ? ` / ${tableData.length}` : ''} ligne{lignesFiltrees.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="rc-table-head-right">
                {filtresActifs.length > 0 && (
                  <button type="button" className="rc-btn rc-btn-ghost" onClick={effacerFiltres}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    Effacer les filtres ({filtresActifs.length})
                  </button>
                )}
                <button type="button" className="rc-btn rc-btn-ghost" onClick={fermerTableau}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Graphiques
                </button>
              </div>
            </div>

            <div className="rc-table-scroll">
              {loading && <div className="rc-empty">Chargement…</div>}
              {!loading && erreur && <div className="rc-empty rc-error">{erreur}</div>}
              {!loading && !erreur && tableData.length === 0 && <div className="rc-empty">Aucune donnée à afficher.</div>}
              {!loading && !erreur && tableData.length > 0 && (
                currentSource === 'offre' ? renderTableOffre() : renderTable()
              )}
              {!loading && !erreur && tableData.length > 0 && lignesFiltrees.length === 0 && (
                <div className="rc-empty">Aucune ligne ne correspond aux filtres.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Relationclient;